import { ReduxAction } from 'app/redux/types';

import { API, getAsyncThunk } from 'app/api';
import { CashoutErrorCode, StakeState } from 'app/lib/staking/constants';

import { EntriesStateType } from '../constants';

// ---

export const PROCESS_CASHOUT_STAKE = 'MY_STAKES:PROCESS_CASHOUT_STAKE';

export type CashoutStake = ReduxAction<typeof PROCESS_CASHOUT_STAKE, {
  id: number;
  amount: number;
  namespace: EntriesStateType;
}>;

export const cashoutStake = (
  id: number,
  amount: number,
  namespace: EntriesStateType,
): CashoutStake => ({
  type: PROCESS_CASHOUT_STAKE,
  payload: { id, amount, namespace },
});

// ---

export const cashOutAmount = getAsyncThunk('MY_STAKES:CASH_OUT_AMOUNT', API.stakes.cashOutAmount);

export type CashOutAmountPending = ReturnType<typeof cashOutAmount.pending>;
export type CashOutAmountSuccess = ReturnType<typeof cashOutAmount.fulfilled>;
export type CashOutAmountRejected = ReturnType<typeof cashOutAmount.rejected>;

// ---

export const SUBMIT_CASHOUT_SUCCESS = 'MY_STAKES:SUBMIT_CASHOUT_SUCCESS';

type SubmitCashoutSuccessResponse = { amount: number };
export type SubmitCashoutSuccess = ReduxAction<
  typeof SUBMIT_CASHOUT_SUCCESS,
  CashoutStake['payload']
> & {
  response: SubmitCashoutSuccessResponse;
};

export const submitCashoutSuccess = (
  response: SubmitCashoutSuccessResponse,
  payload: CashoutStake['payload'],
): SubmitCashoutSuccess => ({
  type: SUBMIT_CASHOUT_SUCCESS,
  response,
  payload,
});

// ---

export const SUBMIT_CASHOUT_FAILURE = 'MY_STAKES:SUBMIT_CASHOUT_FAILURE';

type ResultError = {
  error: CashoutErrorCode;
  amount?: number;
};

export type SubmitCashoutFailure = ReduxAction<
  typeof SUBMIT_CASHOUT_FAILURE,
  CashoutStake['payload']
> & {
  result: ResultError;
};

export const submitCashoutFailure = (
  resultError: ResultError,
  payload: CashoutStake['payload'],
): SubmitCashoutFailure => ({
  type: SUBMIT_CASHOUT_FAILURE,
  result: resultError,
  payload,
});

// ---

export const UPDATE_STAKE = 'MY_STAKES:UPDATE_STAKE';

type UpdateStakePayload = {
  id: number;
  state: StakeState;
  winning: number;
};

export type UpdateStake = ReduxAction<typeof UPDATE_STAKE, UpdateStakePayload>;

export const updateStake = (payload: UpdateStakePayload): UpdateStake => ({
  type: UPDATE_STAKE,
  payload,
});

// ---

export const RESET_CASHOUT_STATE = 'MY_STAKES:RESET_CASHOUT_STATE';

export type ResetCashoutState = ReduxAction<typeof RESET_CASHOUT_STATE, {
  id: number;
}>;

export const resetCashoutState = (stakeId: number): ResetCashoutState => ({
  type: RESET_CASHOUT_STATE,
  payload: { id: stakeId },
});

// ---

export const RESET_CASHOUT_ERROR = 'MY_STAKES:RESET_CASHOUT_ERROR';

export type ResetCashoutError = ReduxAction<typeof RESET_CASHOUT_ERROR, {
  id: number;
}>;

export const resetCashoutError = (stakeId: number): ResetCashoutError => ({
  type: RESET_CASHOUT_ERROR,
  payload: { id: stakeId },
});

// ---

export const SAVE_CASHING_OUT_STAKE = 'MY_STAKES:SAVE_CASHING_OUT_STAKE';

export type SaveCashingOutStake = ReduxAction<typeof SAVE_CASHING_OUT_STAKE, {
  dialogId: string;
  stakeId: number;
}>;

export const saveCashingOutStake = (stakeId: number, dialogId: string): SaveCashingOutStake => ({
  type: SAVE_CASHING_OUT_STAKE,
  payload: { dialogId, stakeId },
});

export const REMOVE_CASHED_OUT_STAKE = 'MY_STAKES:REMOVE_CASHED_OUT_STAKE';

export type RemoveCashedOutStake = ReduxAction<typeof REMOVE_CASHED_OUT_STAKE, { stakeId: number }>;

export const removeCashedOutStake = (stakeId: number): RemoveCashedOutStake => ({
  type: REMOVE_CASHED_OUT_STAKE,
  payload: { stakeId },
});
